import React, { useCallback, useMemo } from 'react';

// Styles & Assets
import styles from './PatientTable.module.css';

// Services
import { getTimestampFirstParam } from '../../../services/Patient';

// Hooks
import { useState } from 'react';
import useTranslate from '../../../hooks/useTranslate';
import { useTranslation } from 'react-i18next';

// Components
import PatientTableCell from '../PatientTableCell/PatientTableCell';
import Modal from '../../UI/Modal/Modal';
import DialogEditVital from '../DialogEditVital/DialogEditVital';
import TablePatientUI from '../../Helpers/TablePatientUI/TablePatientUI';

// Reducers
import { ElementsDispatch } from '../../../interfaces/Reducers';

// Interfaces
import { Parameter, ProfileSheetSectionParameters } from '../../../interfaces/Profile';
import { DataElement, DataElementByHour } from '../../../interfaces/PhysiologicalMonitoring';
import { ITablePatient } from '../../../interfaces/misc';
import useCVTable from '../../../hooks/useCVTable';

const PatientTable: React.FC<{
    section: ProfileSheetSectionParameters,
    data: DataElementByHour[],
    readOnly: boolean
    dispatchElements: React.Dispatch<ElementsDispatch>
}> = ({ section, data, readOnly, dispatchElements }) => {
    const { t } = useTranslation();
    const { translateMiddleware } = useTranslate();
    const { getStylesCV, getCVScoreValue } = useCVTable();

    const [showEditVitalDialog, setShowEditVitalDialog] = useState<boolean>(false);
    const [vitalSignData, setVitalSignData] = useState<{
        param: Parameter
        timestamp: DataElement
    } | null>(null);

    const handleClickCV = (param: Parameter, timestamp: DataElement) => () => {
        setVitalSignData({ param, timestamp });
        setShowEditVitalDialog(true);
    };

    const calculateGlobalValue = useCallback((timestampData: DataElementByHour) => {
        const paramIDS = section.parameters.map(param => param.id ?? '').filter(param => param !== '');
        let acc: number | null = null;

        paramIDS.forEach(paramID => {
            const dataElement = getTimestampFirstParam(paramID, timestampData);
            const valueFirstParam = dataElement?.values?.[paramID];

            if (typeof valueFirstParam === 'number') {
                if (acc === null) acc = 0;
                acc += section.section.section_code === 'cv'
                    ? getCVScoreValue(paramID, valueFirstParam)
                    : valueFirstParam;
            }
        });

        return acc === null ? '-' : Math.round(acc * 100) / 100;
    }, [getCVScoreValue, section.parameters, section.section.section_code]);

    const getPollStyles = useCallback((dataElement: DataElement | undefined, paramID?: string): React.CSSProperties => {
        if (!paramID) return {};
        const backgroundColor = dataElement?.colors?.[paramID];
        return backgroundColor ? { backgroundColor } : {};
    }, []);

    const tablePatient: ITablePatient = useMemo(() => ({
        tableName: section.section.name,
        headerRows: [{
            key: 0,
            name: t('global_value'),
            columns: data.map(timestampData => ({
                key: timestampData.date,
                value: calculateGlobalValue(timestampData).toString()
            }))
        }],
        bodyRows: section.parameters.map(param => {
            const paramId = param.id ?? '';

            return {
                key: paramId,
                name: param.name_translations
                    ? translateMiddleware(param.name_translations)?.text ?? param.name ?? ''
                    : param.name ?? '',
                columns: data.map(timestampData => {
                    const dataElement = paramId ? getTimestampFirstParam(paramId, timestampData) : undefined;
                    const customStyles = section.section.section_code === 'cv'
                        ? dataElement ? getStylesCV(dataElement, param) : {}
                        : getPollStyles(dataElement, paramId);

                    const value = paramId
                        ? dataElement?.comments?.[paramId + '_description'] ??
                          dataElement?.values?.[paramId] ??
                          ''
                        : '';

                    const comment = paramId
                        ? dataElement?.comments?.[paramId + '_comment']
                        : undefined;

                    return (
                        <PatientTableCell
                            key={timestampData.date}
                            customStyles={customStyles}
                            value={value}
                            comment={comment}
                            onClick={
                                section.section.section_code === 'cv' && !readOnly && dataElement
                                    ? handleClickCV(param, dataElement)
                                    : undefined
                            }
                        />
                    );
                })
            };
        })
    }), [calculateGlobalValue, data, getPollStyles, getStylesCV, readOnly, section.parameters, section.section.name, section.section.section_code, t, translateMiddleware]);

    return (
        <>
            <tr>
                <td className={styles.cellTable}>
                    <TablePatientUI tablePatient={tablePatient} />
                </td>
            </tr>
            <Modal show={showEditVitalDialog} setShowModal={setShowEditVitalDialog}>
                <DialogEditVital
                    vitalSign={vitalSignData}
                    modalShow={setShowEditVitalDialog}
                    dispatchElements={dispatchElements}
                />
            </Modal>
        </>
    );
};

export default PatientTable;